import type { HotelTransferStrip } from '@/components/hotels/types';
import type {
  ITransferBoard,
  ShuttleRunView,
  ShuttleRunStatus,
  TransferBoardHotel,
  TransferSeatCounters,
} from '@/lib/disruptions/types';
import { towerT, type TowerT } from './tower-i18n';

// MASS ground transport — pure derivations over the transfer board (no React, no
// IO): the per-hotel shuttle strip the Hotels view draws on every hotel card and
// the plan editor lists. The RUN LIST is the truth (every run push upserts it over
// the WS); the plan's own counters are only a fallback for a hotel with no runs
// cached yet. Counts + masked labels only — never a passenger record.

export const RUN_ORDER: readonly ShuttleRunStatus[] = [
  'BOARDING',
  'VEHICLE_ASSIGNED',
  'REQUESTED',
  'DEPARTED',
  'PLANNED',
  'ARRIVED',
  'FAILED',
  'CANCELLED',
];

/** Runs still to leave (the operator can cancel / re-cut them). */
export const RUN_NOT_DEPARTED: ReadonlySet<ShuttleRunStatus> = new Set<ShuttleRunStatus>([
  'PLANNED',
  'REQUESTED',
  'VEHICLE_ASSIGNED',
  'BOARDING',
]);
export const RUN_ON_ROAD: ReadonlySet<ShuttleRunStatus> = new Set<ShuttleRunStatus>([
  'REQUESTED',
  'VEHICLE_ASSIGNED',
  'BOARDING',
  'DEPARTED',
]);

export function runStatusLabel(status: ShuttleRunStatus, t: TowerT = towerT): string {
  return t(`transfer.runStatus.${status}`, { defaultValue: String(status).toLowerCase() });
}

export function runTone(status: ShuttleRunStatus): 'ok' | 'run' | 'warn' | 'danger' | 'muted' {
  switch (status) {
    case 'ARRIVED':
      return 'ok';
    case 'REQUESTED':
    case 'VEHICLE_ASSIGNED':
    case 'BOARDING':
    case 'DEPARTED':
      return 'run';
    case 'FAILED':
      return 'danger';
    case 'CANCELLED':
      return 'muted';
    default:
      return 'warn';
  }
}

export function vehicleLabel(run: ShuttleRunView): string {
  const cls = (run.vehicle?.class ?? run.vehicleClass).toUpperCase();
  const cap = run.vehicle?.capacity ?? run.capacity;
  return `${cls} ${cap}`;
}

/** Runs of ONE hotel + leg, ordered for the list (moving first, then planned, then done). */
export function runsOfHotel(
  runs: readonly ShuttleRunView[],
  hotelUrn: string,
  leg: ShuttleRunView['leg'] = 'AIRPORT_TO_HOTEL',
): ShuttleRunView[] {
  return runs
    .filter((r) => r.hotelUrn === hotelUrn && r.leg === leg)
    .sort((a, b) => a.seq - b.seq);
}

/**
 * Seats the plan holds for ONE leg — the leg's own demand. `ownMeans` is excluded:
 * those passengers left the shuttle problem, so counting them would keep a hotel
 * permanently "waiting" for a vehicle nobody needs.
 */
function legSeatDemand(
  hotel: TransferBoardHotel,
  leg: ShuttleRunView['leg'],
): number | undefined {
  if (!hotel.seats) return undefined;
  // `TransferLegCounters` tolerates a FLAT counter as well as a per-leg map, and
  // only the per-leg shape can answer a per-leg question — a flat one is "unknown
  // for this leg", never silently attributed to the leg being drawn.
  const perLeg = hotel.seats as Partial<Record<ShuttleRunView['leg'], TransferSeatCounters>>;
  const s = perLeg[leg];
  if (!s || typeof s.pending !== 'number') return undefined;
  return s.pending + s.assigned + s.boarded + s.arrived + s.noShow;
}

/**
 * The strip of ONE hotel on ONE leg.
 *
 * `leg` is a parameter, not a constant: the desk draws this strip twice — outbound
 * (airport → hotels) and return (hotels → airport) — and a hardcoded outbound made
 * the return board render the outbound's numbers, so both legs showed an identical
 * row. `returnRuns` stays HOTEL_TO_AIRPORT whichever leg is being cut, because its
 * consumers (the RETURN lens filter, the expanded row) ask it that exact question.
 */
function stripOf(
  hotel: TransferBoardHotel,
  runs: readonly ShuttleRunView[],
  leg: ShuttleRunView['leg'] = 'AIRPORT_TO_HOTEL',
): HotelTransferStrip {
  const out = runsOfHotel(runs, hotel.hotelUrn, leg);
  const back = runsOfHotel(runs, hotel.hotelUrn, 'HOTEL_TO_AIRPORT');
  const count = (st: ShuttleRunStatus) => out.filter((r) => r.status === st).length;
  const runsBy = {
    planned: count('PLANNED'),
    dispatched: count('REQUESTED') + count('VEHICLE_ASSIGNED'),
    boarding: count('BOARDING'),
    inTransit: count('DEPARTED'),
    arrived: count('ARRIVED'),
    failed: count('FAILED'),
    cancelled: count('CANCELLED'),
  };
  const paxArrived = out
    .filter((r) => r.status === 'ARRIVED')
    .reduce((a, r) => a + (r.arrivedCount || r.boardedCount || 0), 0);
  const paxOnRoad = out
    .filter((r) => RUN_ON_ROAD.has(r.status))
    .reduce((a, r) => a + (r.status === 'DEPARTED' ? r.boardedCount || r.assignedCount : r.assignedCount), 0);
  const noShow = out.reduce((a, r) => a + r.noShowCount, 0);
  // The roster's `paxNeeded` counts the OUTBOUND need only; the return leg takes
  // its demand from the plan's own per-leg seat counters.
  const demand =
    legSeatDemand(hotel, leg) ?? (leg === 'AIRPORT_TO_HOTEL' ? hotel.paxNeeded : 0);
  const paxToMove = Math.max(demand, paxArrived + paxOnRoad);
  const paxWaiting = Math.max(0, paxToMove - paxArrived - paxOnRoad - noShow);
  const next = out
    .filter((r) => RUN_NOT_DEPARTED.has(r.status))
    .map((r) => r.scheduledAt)
    .filter(Boolean)
    .sort()[0];
  const nextPickupAt = next ?? hotel.nextPickupAt;
  return {
    hotelUrn: hotel.hotelUrn,
    hotelName: hotel.hotelName,
    paxToMove,
    paxArrived,
    paxOnRoad,
    paxWaiting,
    noShow,
    vehiclesPlanned: out.filter((r) => r.status !== 'CANCELLED').length,
    runs: runsBy,
    returnRuns: {
      scheduled: back.filter((r) => RUN_NOT_DEPARTED.has(r.status)).length,
      onRoad: back.filter((r) => r.status === 'DEPARTED').length,
      done: back.filter((r) => r.status === 'ARRIVED').length,
    },
    ...(nextPickupAt ? { nextPickupAt } : {}),
    ...(hotel.pickupPoint?.label ? { pickupPoint: hotel.pickupPoint.label } : {}),
  };
}

/** The per-hotel strips of a board for ONE leg (keyed by hotel URN), hotels with
 *  the most to move first. */
export function stripsOf(
  board: ITransferBoard | null | undefined,
  leg: ShuttleRunView['leg'] = 'AIRPORT_TO_HOTEL',
): Map<string, HotelTransferStrip> {
  const map = new Map<string, HotelTransferStrip>();
  if (!board) return map;
  const hotels = [...board.hotels];
  // Hotels only the run list knows (a run pushed before the board row landed).
  for (const r of board.runs) {
    if (!hotels.some((h) => h.hotelUrn === r.hotelUrn))
      hotels.push({ hotelUrn: r.hotelUrn, hotelName: r.hotelName ?? shortHotel(r.hotelUrn), paxNeeded: 0, pnrs: 0 });
  }
  const strips = hotels.map((h) => stripOf(h, board.runs, leg));
  strips.sort((a, b) => b.paxToMove - b.paxArrived - (a.paxToMove - a.paxArrived) || b.paxToMove - a.paxToMove);
  for (const s of strips) map.set(s.hotelUrn, s);
  return map;
}

/**
 * Readable hotel label from a URN when the board carried no name.
 *
 * Hotel URNs are `urn:hotel:<id>:vendor:<vendor>`, so the LAST segment is the
 * supplier — taking it rendered every hotel of one supplier as the same word
 * ("mock", "mock", "mock") and made the desk unreadable. The identity segment is
 * everything between the entity and the `vendor` tag.
 */
export function shortHotel(hotelUrn: string): string {
  const parts = hotelUrn.split(':');
  const vendorAt = parts.indexOf('vendor');
  const id =
    parts[0] === 'urn'
      ? (vendorAt > 2 ? parts.slice(2, vendorAt) : parts.slice(2)).join(':')
      : (parts.pop() ?? hotelUrn);
  return id.replace(/[-_]+/g, ' ').trim() || hotelUrn;
}

/** Board totals for the panel header. */
export function boardTotals(strips: Iterable<HotelTransferStrip>): {
  hotels: number;
  paxToMove: number;
  paxArrived: number;
  paxOnRoad: number;
  paxWaiting: number;
  noShow: number;
  vehiclesPlanned: number;
  vehiclesOnRoad: number;
  failed: number;
} {
  const t = { hotels: 0, paxToMove: 0, paxArrived: 0, paxOnRoad: 0, paxWaiting: 0, noShow: 0, vehiclesPlanned: 0, vehiclesOnRoad: 0, failed: 0 };
  for (const s of strips) {
    t.hotels += 1;
    t.paxToMove += s.paxToMove;
    t.paxArrived += s.paxArrived;
    t.paxOnRoad += s.paxOnRoad;
    t.paxWaiting += s.paxWaiting;
    t.noShow += s.noShow;
    t.vehiclesPlanned += s.vehiclesPlanned;
    t.vehiclesOnRoad += s.runs.dispatched + s.runs.boarding + s.runs.inTransit;
    t.failed += s.runs.failed;
  }
  return t;
}
